import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, db } from "@/lib/firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { DEFAULT_PERMISSIONS } from "@/lib/firestore";

const AuthContext = createContext(null);

function authErrorMessage(e) {
  switch (e.code) {
    case "auth/email-already-in-use": return "Email already registered";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found": return "Invalid email or password";
    case "auth/weak-password": return "Password is too weak";
    case "auth/invalid-email": return "Invalid email address";
    default: return e.message || "Something went wrong. Please try again.";
  }
}

async function fetchProfile(uid) {
  const snap = await getDoc(doc(db, "users", uid));
  return snap.exists() ? snap.data() : null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, obj = user
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) { setUser(false); return; }
      const profile = await fetchProfile(fbUser.uid);
      // Firebase Auth has no concept of "pending approval" — that's enforced
      // here against the Firestore profile on every auth-state change, so a
      // user revoked mid-session gets signed out on their next refresh too.
      if (!profile || profile.status !== "approved") {
        await fbSignOut(auth);
        setUser(false);
        return;
      }
      setUser(profile);
    });
    return unsub;
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const profile = await fetchProfile(cred.user.uid);
      if (!profile || profile.status === "pending" || profile.status === "rejected") {
        await fbSignOut(auth);
        const msg = !profile
          ? "Account not found"
          : profile.status === "pending"
          ? "Account pending admin approval"
          : "Account has been rejected";
        setError(msg);
        return { ok: false, error: msg };
      }
      setUser(profile);
      return { ok: true };
    } catch (e) {
      const msg = authErrorMessage(e);
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const register = async (email, password, name) => {
    setError("");
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.toLowerCase(), password);
      const profile = {
        id: cred.user.uid,
        email: email.toLowerCase(),
        name,
        role: "user",
        status: "pending", // awaits admin approval
        permissions: DEFAULT_PERMISSIONS,
        created_at: new Date().toISOString(),
      };
      await setDoc(doc(db, "users", cred.user.uid), profile);
      // Registering shouldn't sign you in — admin approval is required first.
      await fbSignOut(auth);
      return { ok: true };
    } catch (e) {
      const msg = authErrorMessage(e);
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    await fbSignOut(auth);
    setUser(false);
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: authErrorMessage(e) };
    }
  };

  const reload = useCallback(async () => {
    if (auth.currentUser) {
      const profile = await fetchProfile(auth.currentUser.uid);
      if (profile) setUser(profile);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, resetPassword, reload }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
